import React, { useEffect } from 'react';

import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AddIcon from '@material-ui/icons/Add';
import dayjs from 'dayjs';
import { useModal } from 'react-modal-hook';

import { useAppDispatch, useAppSelector } from 'store';
import {
  ContactFunnelOpportunity,
  contatosSelector,
  fetchFunnelsAndOpportunities,
} from 'store/reducers/contatos';
import { formatCurrency } from 'utils/currencyUtils';

import ContactFunnelTabPanelSkeleton from './ContactFunnelTabPanelSkeleton';
import CreateOpportunityForContactDrawer from './CreateOpportunityForContactDrawer';
import OpportunityDetailsDrawer from './OpportunityDetailsDrawer';

const useStyles = makeStyles(({ spacing, palette }) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(1),
  },
  headerTitle: {
    fontWeight: 500,
    color: alpha(palette.primary.main, 0.7),
  },
  funnelsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
  },
  funnelCard: {
    padding: spacing(2),
    backgroundColor: palette.background.default,
  },
  funnelTitle: {
    fontWeight: 500,
  },
  opportunitiesList: {
    marginTop: spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
  },
  opportunityCard: {
    padding: spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(0.5),
    cursor: 'pointer',
  },
  opportunityHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(1),
  },
  stageName: {
    fontWeight: 500,
  },
  durationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
  divider: {
    marginBottom: spacing(1),
    marginLeft: spacing(-2),
    marginRight: spacing(-2),
  },
  tagsContainer: {
    display: 'flex',
    alignItems: 'center',
    gridGap: spacing(0.5),
  },
  valueSourceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  opportunityValue: {
    fontWeight: 500,
  },
  metadataContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  secondaryText: {
    color: alpha(palette.text.primary, 0.7),
  },
}));

interface ContactFunnelTabPanelProps {
  contactId: string;
}

function ContactFunnelTabPanel({ contactId }: ContactFunnelTabPanelProps) {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const theme = useTheme();

  const [selectedFunnelId, setSelectedFunnelId] = React.useState<string>();
  const [selectedOpportunity, setSelectedOpportunity] =
    React.useState<ContactFunnelOpportunity | null>(null);

  const { funnelsAndOpportunities, loadingFunnelsAndOpportunities } =
    useAppSelector(contatosSelector);

  const handleRefreshOpportunities = () => {
    dispatch(fetchFunnelsAndOpportunities(contactId));
  };

  const [showCreateOpportunityDrawer, hideCreateOpportunityDrawer] = useModal(
    ({ in: open }) => {
      return (
        <CreateOpportunityForContactDrawer
          contatoId={contactId}
          open={open}
          onSuccess={handleRefreshOpportunities}
          onClose={hideCreateOpportunityDrawer}
        />
      );
    },
    [contactId],
  );

  const handleSuccessDeleteOpportunity = () => {
    hideOpportunityDetailsDrawer();
    handleRefreshOpportunities();
  };

  const [showOpportunityDetailsDrawer, hideOpportunityDetailsDrawer] = useModal(
    ({ in: open }) => {
      if (!selectedFunnelId || !selectedOpportunity) {
        return null;
      }

      return (
        <OpportunityDetailsDrawer
          open={open}
          onClose={hideOpportunityDetailsDrawer}
          opportunity={selectedOpportunity}
          funnelId={selectedFunnelId}
          onSuccessDeleteOpportunity={handleSuccessDeleteOpportunity}
        />
      );
    },
    [contactId, selectedFunnelId, selectedOpportunity],
  );

  const handleOpenOpportunityDetails = (
    funnelId: string,
    opportunity: ContactFunnelOpportunity,
  ) => {
    setSelectedFunnelId(funnelId);
    setSelectedOpportunity(opportunity);
    showOpportunityDetailsDrawer();
  };

  useEffect(() => {
    dispatch(fetchFunnelsAndOpportunities(contactId));
  }, [contactId, dispatch]);

  const totalOpportunities = funnelsAndOpportunities.reduce(
    (acc, item) => acc + item.funnel.opportunities.length,
    0,
  );

  if (loadingFunnelsAndOpportunities) {
    return <ContactFunnelTabPanelSkeleton />;
  }

  return (
    <Box>
      {/* HEADER */}
      <Box className={classes.header}>
        <Typography className={classes.headerTitle} variant="body2">
          Oportunidades ativas ({totalOpportunities})
        </Typography>

        <Button onClick={showCreateOpportunityDrawer} startIcon={<AddIcon />}>
          Criar nova oportunidade
        </Button>
      </Box>

      {/* FUNNELS LIST */}
      <Box className={classes.funnelsList}>
        {funnelsAndOpportunities.map(item => (
          <Card
            className={classes.funnelCard}
            elevation={0}
            key={item.funnel.id}
          >
            {/* FUNNEL TITLE */}
            <Typography className={classes.funnelTitle} variant="body2">
              {item.funnel.code} - {item.funnel.name}
            </Typography>

            {/* OPPORTUNITIES LIST */}
            <Box className={classes.opportunitiesList}>
              {item.funnel.opportunities.map(opportunity => (
                <Card
                  onClick={() =>
                    handleOpenOpportunityDetails(item.funnel.id, opportunity)
                  }
                  className={classes.opportunityCard}
                  elevation={0}
                  key={opportunity.id}
                >
                  {/* OPPORTUNITY HEADER */}
                  <Box className={classes.opportunityHeader}>
                    <Typography className={classes.stageName} variant="body2">
                      {opportunity.stageName}
                    </Typography>

                    <Box className={classes.durationContainer}>
                      <AccessTimeIcon
                        className={classes.secondaryText}
                        fontSize="small"
                      />
                      <Typography
                        className={classes.secondaryText}
                        variant="caption"
                      >
                        {dayjs(opportunity.createdAt).fromNow()}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider className={classes.divider} />

                  {/* TAGS */}
                  {opportunity.tags && opportunity.tags.length > 0 && (
                    <Box className={classes.tagsContainer}>
                      {opportunity.tags.map(tag => (
                        <Chip
                          style={{
                            fontSize: theme.typography.pxToRem(10),
                            color: theme.palette.getContrastText(tag.color),
                            backgroundColor: tag.color,
                          }}
                          key={tag.id}
                          label={tag.name}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}

                  {/* DESCRIPTION */}
                  {opportunity.description && (
                    <Typography variant="body2">
                      {opportunity.description}
                    </Typography>
                  )}

                  {/* VALUE AND SOURCE */}
                  <Box className={classes.valueSourceContainer}>
                    <Typography
                      className={classes.opportunityValue}
                      color="primary"
                      variant="body2"
                    >
                      {formatCurrency(opportunity.opportunityValue)}
                    </Typography>

                    <Typography
                      className={classes.secondaryText}
                      variant="body2"
                    >
                      {opportunity.sourceName}
                    </Typography>
                  </Box>

                  {/* STAGE STATUS */}
                  {opportunity.stageStatus && (
                    <Box>
                      <Chip
                        size="small"
                        style={{
                          fontSize: theme.typography.pxToRem(10),
                        }}
                        label={opportunity.stageStatus}
                      />
                    </Box>
                  )}

                  {/* METADATA */}
                  <Box className={classes.metadataContainer}>
                    <Typography
                      className={classes.secondaryText}
                      variant="body2"
                    >
                      Criado:{' '}
                      {dayjs(opportunity.createdAt).format(
                        'DD/MM/YY[, às] HH:mm',
                      )}
                    </Typography>

                    <Typography
                      className={classes.secondaryText}
                      variant="body2"
                    >
                      Código: {opportunity.code}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export default ContactFunnelTabPanel;
